import getCart from '../lib/getCart';
import addToCart from '../lib/addToCart';
import changeCart from '../lib/changeCart';
import { closeProductContentDrawer } from './product-content-drawers';
import {
  initWarrantySelection,
  getWarrantySelection,
  setWarrantySelection,
  clearWarrantySelection,
  findWarrantyLineItem,
  getWarrantyCartProperties,
  getWarrantyQuantity,
  markWarrantyInCartFromCart,
  persistWarrantySelectionForProduct,
  clearWarrantyStorageForProduct,
} from '../lib/warrantySelection';

const SELECTOR_WARRANTY_VARIANT = '[data-warranty-variant-select]';
const SELECTOR_PRODUCT_WARRANTY = '[data-product-warranty]';
const CART_UPDATED_EVENT = 'cart:updated';

const formatSelectedLabel = (title, template) => {
  if (!template) return `${title} selected`;
  return template.replace('{{ title }}', title);
};

const getConfig = configScript => {
  if (!configScript) return null;
  try {
    return JSON.parse(configScript.textContent);
  } catch (error) {
    console.error('Failed to parse warranty drawer config:', error);
    return null;
  }
};

const getSelectedVariantFromSelect = select => {
  const option = select.options[select.selectedIndex];
  if (!option) return null;
  return {
    variantId: Number(option.value),
    variantTitle: option.dataset.variantTitle || option.textContent.trim(),
    variantPriceFormatted: option.dataset.variantPriceFormatted || '',
  };
};

const updateSelectDisplay = select => {
  const wrap = select.closest('[data-warranty-select-wrap]');
  if (!wrap) return;
  const option = select.options[select.selectedIndex];
  const titleEl = wrap.querySelector('[data-warranty-select-title]');
  const priceEl = wrap.querySelector('[data-warranty-select-price]');
  if (titleEl) {
    titleEl.textContent = option?.dataset.variantTitle || '';
  }
  if (priceEl) {
    priceEl.textContent = option?.dataset.variantPriceFormatted || '';
  }
};

const updateBanner = (banner, selection, labelTemplate) => {
  if (!banner) return;
  const labelEl = banner.querySelector('[data-warranty-selection-label]');
  const priceEl = banner.querySelector('[data-warranty-selection-price]');
  if (labelEl) {
    labelEl.textContent = formatSelectedLabel(selection.variantTitle, labelTemplate);
  }
  if (priceEl) {
    priceEl.textContent = selection.variantPriceFormatted || '';
  }
};

const getDrawerScope = drawerId =>
  document.querySelector(`[data-warranty-content-drawer][data-product-content-drawer="${drawerId}"]`) ||
  document;

const closeWarrantyDrawer = drawerId => {
  const dialog = document.querySelector(`[data-product-content-drawer="${drawerId}"]`);
  const trigger = document.querySelector(`[data-product-content-drawer-open="${drawerId}"]`);
  closeProductContentDrawer(dialog, trigger);
};

const setVisible = (element, visible) => {
  element.hidden = !visible;
  element.classList.toggle('hidden', !visible);
};

const syncUi = (root, config, labelTemplate) => {
  const selection = getWarrantySelection();
  const hasSelection = Boolean(selection.variantId);
  const showPicker = selection.isEditing || !hasSelection;
  const showSelected = hasSelection && !selection.isEditing;
  const drawerScope = getDrawerScope(config.drawerId);

  drawerScope.querySelectorAll('[data-warranty-picker]').forEach(picker => {
    setVisible(picker, showPicker);
  });

  drawerScope.querySelectorAll('[data-warranty-add-cover]').forEach(button => {
    setVisible(button, showPicker);
  });

  drawerScope.querySelectorAll('[data-warranty-drawer-footer]').forEach(footer => {
    setVisible(footer, showPicker);
  });

  drawerScope.querySelectorAll('[data-warranty-selected]').forEach(selected => {
    setVisible(selected, showSelected);
    if (showSelected) {
      updateBanner(selected.querySelector('[data-warranty-selection-banner]'), selection, labelTemplate);
    }
  });

  const pdpSelection = root.querySelector('[data-warranty-pdp-selection]');
  if (pdpSelection) {
    setVisible(pdpSelection, showSelected);
    if (showSelected) {
      updateBanner(pdpSelection.querySelector('[data-warranty-selection-banner]'), selection, labelTemplate);
    }
  }

  drawerScope.querySelectorAll(SELECTOR_WARRANTY_VARIANT).forEach(select => {
    if (selection.variantId) {
      select.value = String(selection.variantId);
    }
    updateSelectDisplay(select);
  });
};

const syncFromCart = async (root, config, labelTemplate) => {
  const cart = await getCart();
  const lineItem = findWarrantyLineItem(cart, config);
  if (lineItem) {
    const variant = config.variants.find(v => v.id === lineItem.variant_id);
    setWarrantySelection({
      variantId: lineItem.variant_id,
      variantTitle: variant?.title || lineItem.variant_title || lineItem.title,
      variantPriceFormatted: variant?.priceFormatted || '',
      inCart: true,
      lineItemKey: lineItem.key,
      isEditing: false,
    });
  } else if (getWarrantySelection().inCart) {
    clearWarrantySelection({ clearStorage: false });
  }
  syncUi(root, config, labelTemplate);
};

const stageWarrantySelection = (config, selected) => {
  setWarrantySelection({
    parentProductId: config.parentProductId,
    variantId: selected.variantId,
    variantTitle: selected.variantTitle,
    variantPriceFormatted: selected.variantPriceFormatted,
    isEditing: false,
    inCart: false,
  });
  persistWarrantySelectionForProduct(config.parentProductId, selected);
};

const updateWarrantyInCart = async (config, selected) => {
  const quantity = getWarrantyQuantity();
  await addToCart([
    {
      id: selected.variantId,
      quantity,
      properties: getWarrantyCartProperties(config.parentProductId),
    },
  ]);
  setWarrantySelection({
    ...selected,
    parentProductId: config.parentProductId,
    isEditing: false,
  });
  await markWarrantyInCartFromCart(config.parentProductId);
  persistWarrantySelectionForProduct(config.parentProductId, selected);
  document.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT));
};

const handleAddCover = async (root, config, labelTemplate) => {
  const drawerScope = getDrawerScope(config.drawerId);
  const select = drawerScope.querySelector(SELECTOR_WARRANTY_VARIANT);
  if (!select) return;

  const selected = getSelectedVariantFromSelect(select);
  if (!selected?.variantId) return;

  const current = getWarrantySelection();

  if (current.inCart && current.lineItemKey) {
    if (current.variantId === selected.variantId) {
      setWarrantySelection({ isEditing: false });
      syncUi(root, config, labelTemplate);
      closeWarrantyDrawer(config.drawerId);
      return;
    }
    try {
      await changeCart(current.lineItemKey, 0);
      await updateWarrantyInCart(config, selected);
      syncUi(root, config, labelTemplate);
      closeWarrantyDrawer(config.drawerId);
    } catch (error) {
      console.error('Error updating warranty cover in cart:', error);
    }
    return;
  }

  stageWarrantySelection(config, selected);
  syncUi(root, config, labelTemplate);
  closeWarrantyDrawer(config.drawerId);
};

const handleRemove = async (root, config, labelTemplate) => {
  const current = getWarrantySelection();
  if (current.inCart && current.lineItemKey) {
    await changeCart(current.lineItemKey, 0);
    document.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT));
  }
  clearWarrantySelection();
  clearWarrantyStorageForProduct(config.parentProductId);
  syncUi(root, config, labelTemplate);
};

const getActiveWarrantyContext = () => {
  const root = document.querySelector(SELECTOR_PRODUCT_WARRANTY);
  if (!root) return null;

  const configScript = document.querySelector('[data-warranty-content-drawer] [data-warranty-drawer-config]');
  const config = getConfig(configScript);
  if (!config?.drawerId) return null;

  return {
    root,
    config,
    labelTemplate: root.dataset.warrantySelectedLabel || '{{ title }} selected',
  };
};

let warrantyDocumentListenersBound = false;

const bindWarrantyDocumentListeners = () => {
  if (warrantyDocumentListenersBound) return;
  warrantyDocumentListenersBound = true;

  document.addEventListener('click', async event => {
    const inWarrantyUi =
      event.target.closest(SELECTOR_PRODUCT_WARRANTY) ||
      event.target.closest('[data-warranty-content-drawer]');
    if (!inWarrantyUi) return;

    const ctx = getActiveWarrantyContext();
    if (!ctx) return;

    const { root, config, labelTemplate } = ctx;

    const addBtn = event.target.closest('[data-warranty-add-cover]');
    if (addBtn) {
      event.preventDefault();
      await handleAddCover(root, config, labelTemplate);
      return;
    }

    const editBtn = event.target.closest('[data-warranty-edit]');
    if (editBtn) {
      event.preventDefault();
      setWarrantySelection({ isEditing: true });
      syncUi(root, config, labelTemplate);
      getDrawerScope(config.drawerId).querySelector(SELECTOR_WARRANTY_VARIANT)?.focus();
      return;
    }

    const removeBtn = event.target.closest('[data-warranty-remove]');
    if (removeBtn) {
      event.preventDefault();
      await handleRemove(root, config, labelTemplate);
    }
  });

  document.addEventListener('warranty:change', () => {
    const ctx = getActiveWarrantyContext();
    if (ctx) syncUi(ctx.root, ctx.config, ctx.labelTemplate);
  });

  document.addEventListener(CART_UPDATED_EVENT, () => {
    const ctx = getActiveWarrantyContext();
    if (ctx) syncFromCart(ctx.root, ctx.config, ctx.labelTemplate);
  });

  document.addEventListener('product-content-drawer:open', event => {
    const ctx = getActiveWarrantyContext();
    if (!ctx || event.detail?.drawerId !== ctx.config.drawerId) return;
    getDrawerScope(ctx.config.drawerId)
      .querySelectorAll(SELECTOR_WARRANTY_VARIANT)
      .forEach(select => {
        updateSelectDisplay(select);
      });
  });
};

const bindWarrantyDrawerClose = config => {
  const dialog = document.querySelector(
    `[data-warranty-content-drawer][data-product-content-drawer="${config.drawerId}"]`
  );
  if (!dialog || dialog.dataset.warrantyCloseBound === 'true') return;

  dialog.dataset.warrantyCloseBound = 'true';
  dialog.addEventListener('close', () => {
    if (getWarrantySelection().isEditing) {
      setWarrantySelection({ isEditing: false });
    }
  });
};

const initProductWarrantyDrawer = (scope = document) => {
  const root = scope.querySelector(SELECTOR_PRODUCT_WARRANTY);
  if (!root || root.dataset.productWarrantyInit === 'true') return;

  const configScript =
    scope.querySelector('[data-warranty-drawer-config]') ||
    document.querySelector('[data-warranty-content-drawer] [data-warranty-drawer-config]');
  if (!configScript) return;

  const config = getConfig(configScript);
  if (!config?.drawerId) return;

  const labelTemplate = root.dataset.warrantySelectedLabel || '{{ title }} selected';

  initWarrantySelection({
    parentProductId: config.parentProductId,
    variantIds: config.variantIds,
  });

  root.dataset.productWarrantyInit = 'true';
  bindWarrantyDocumentListeners();
  bindWarrantyDrawerClose(config);

  const drawerScope = getDrawerScope(config.drawerId);

  drawerScope.querySelectorAll(SELECTOR_WARRANTY_VARIANT).forEach(select => {
    select.addEventListener('change', () => updateSelectDisplay(select));
    updateSelectDisplay(select);
  });

  syncUi(root, config, labelTemplate);
  syncFromCart(root, config, labelTemplate);
};

export default initProductWarrantyDrawer;
